import { useState, useEffect } from 'react';
import Image from 'next/legacy/image';
import { CustomerProfile, CustomerReviewInstance, ReviewApi } from 'types';
import { BrandsList } from 'components/Brands';
import { getFormattedDate, formatDateAgo } from 'utils/formatters';
import { getReviews } from 'api';
import { NextLink } from 'components/NextLink';
import { ReviewItem } from 'components/ReviewItem';
import { LazyLoader } from 'components/LazyLoader';
import { USER_COVER_PLACEHOLDER_LINK } from 'consts';

import cx from 'classnames';
import styles from '../UserProfile.module.scss';

export type AboutTabProps = {
  profile: CustomerProfile;
  // setSnackbarMessage: Dispatch<SnackbarProps | null>;
};

export type ReviewTab = {
  data: CustomerReviewInstance[];
  count: number;
};

export type ReviewTabs = Record<ReviewApi, ReviewTab>;

const baseReviewsLimit = 5;

export const AboutTab = ({ profile }: AboutTabProps) => {
  const [currentReviewTab, setCurrentReviewTab] = useState<ReviewApi>('received');
  const [reviews, setReviews] = useState<ReviewTabs>({} as ReviewTabs);
  const [receivedPage, setReceivedPage] = useState(1);
  const [receivedLastPage, setReceivedLastPage] = useState(1);
  const [givenPage, setGivenPage] = useState(1);
  const [givenLastPage, setGivenLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadReviews().then();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReviews = async () => {
    const receivedRequest = getReviews({ page: 1, limit: baseReviewsLimit, reviewed_id: profile.id }, 'received');
    const givenRequest = getReviews({ page: 1, limit: baseReviewsLimit, reviewer_id: profile.id }, 'given');

    const [received, given] = await Promise.all([receivedRequest, givenRequest]);

    if (received.data && given.data) {
      setReceivedLastPage(received.meta?.last_page || 1);
      setGivenLastPage(given.meta?.last_page || 1);
      setReviews({
        received: {
          data: received.data || [],
          count: received.meta?.total || 0,
        },
        given: {
          data: given.data || [],
          count: given.meta?.total || 0,
        },
      });
    }
  };

  const loadMore = async () => {
    return loadMoreReviews();
  };

  const loadMoreReviews = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const moreReviewsResponse = await getReviews(
      {
        page: (currentReviewTab === 'given' ? givenPage : receivedPage) + 1,
        limit: baseReviewsLimit,
        [`reviewe${currentReviewTab === 'given' ? 'r' : 'd'}_id`]: profile.id,
      },
      currentReviewTab,
    );

    if (moreReviewsResponse.data) {
      if (currentReviewTab === 'given') {
        setGivenPage((page) => page + 1);
        setGivenLastPage(moreReviewsResponse.meta?.last_page || 1);
      } else {
        setReceivedPage((page) => page + 1);
        setReceivedLastPage(moreReviewsResponse.meta?.last_page || 1);
      }

      setReviews((previous) => ({
        ...previous,
        [currentReviewTab]: {
          data: [...previous[currentReviewTab].data, ...(moreReviewsResponse?.data || [])],
          count: moreReviewsResponse.meta?.total || 0,
        },
      }));
    }
    setIsLoading(false);
  };

  return (
    <div className={styles.tabContentWrapper}>
      <div className={cx(styles.content, styles.contentS)}>
        <div className={styles.contentBlock}>
          <div className={styles.contentBlockHeader}>
            <div className={styles.contentBlockHeaderTitle}>
              About {profile.is_deleted ? 'Deleted User' : profile.full_name}
            </div>
          </div>
          <div className={styles.contentBlockBody}>
            <div className={styles.about}>
              <div className={styles.aboutItem}>
                <div className={styles.aboutItemIcon}>
                  <Image src="/icons/time/CalendarBlankLight.svg" alt="" width={24} height={24} />
                </div>
                <div className={styles.aboutItemData}>
                  <div className={styles.aboutItemLabel}>Member since</div>
                  <div className={styles.aboutItemValue}>
                    {getFormattedDate(+profile.created_at * 1000, 'MONTH YYYY')}
                  </div>
                </div>
              </div>
              <div className={styles.aboutItem}>
                <div className={styles.aboutItemIcon}>
                  <Image src="/icons/commerce/ShoppingBagLight.svg" alt="" width={24} height={24} />
                </div>
                <div className={styles.aboutItemData}>
                  <div className={styles.aboutItemLabel}>Average Ship Time</div>
                  <div className={styles.aboutItemValue}>
                    {profile.ship_time_avg
                      ? `${profile.ship_time_avg} Hour${profile.ship_time_avg !== 1 ? 's' : ''}`
                      : '-'}
                  </div>
                </div>
              </div>
              <div className={styles.aboutItem}>
                <div className={styles.aboutItemIcon}>
                  <Image src="/icons/time/ClockLight.svg" alt="" width={24} height={24} />
                </div>
                <div className={styles.aboutItemData}>
                  <div className={styles.aboutItemLabel}>Last seen Online</div>
                  <div className={styles.aboutItemValue}>
                    {!profile.last_seen_at ? 'Never' : formatDateAgo(+profile.last_seen_at)}
                  </div>
                </div>
              </div>
              <div className={styles.aboutItem}>
                <div className={styles.aboutItemIcon}>
                  <Image src="/icons/security/InfoLight.svg" alt="" width={24} height={24} />
                </div>
                <div className={styles.aboutItemData}>
                  <div className={styles.aboutItemLabel}>Description</div>
                  <div className={styles.aboutItemValue}>{profile.description || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={cx(styles.contentBlock, styles.sidebarBrandsBlock)}>
          <div className={styles.contentBlockHeader}>
            <div className={styles.contentBlockHeaderTitle}>
              <span>Following Brands</span>
            </div>
            <NextLink href="/brands" className={cx(styles.contentViewAll, styles.tabShow)} title="View All" />
          </div>
          <div className={styles.contentBlockBody}>
            <BrandsList brands={profile.brands} type="halfed" />
            <br className={styles.tabHide} />
            <NextLink href="/brands" className={cx(styles.contentViewAll, styles.tabHide)} title="View All Brands" />
          </div>
        </div>
      </div>

      <div className={cx(styles.content, styles.contentL)}>
        <div className={styles.contentBlock}>
          {(!!reviews?.received?.data?.length || !!reviews?.given?.data?.length) && (
            <>
              <div className={cx(styles.contentBlockHeader, styles.followersHeader)}>
                <div className={styles.contentBlockHeaderTitle}>Reviews</div>
              </div>
              <div className={styles.contentBlockBody}>
                <div className={styles.reviewBar}>
                  <div className={styles.reviewTabs}>
                    <p
                      className={cx(styles.reviewTabItem, { [styles.active]: currentReviewTab === 'received' })}
                      onClick={() => setCurrentReviewTab('received')}
                    >
                      Received ({reviews?.received.count})
                    </p>
                    <p
                      className={cx(styles.reviewTabItem, { [styles.active]: currentReviewTab === 'given' })}
                      onClick={() => setCurrentReviewTab('given')}
                    >
                      Given ({reviews?.given.count})
                    </p>
                  </div>
                </div>
                {reviews[currentReviewTab].data.map((review) => (
                  <ReviewItem
                    key={review.id}
                    from={currentReviewTab === 'received'}
                    author={review[currentReviewTab === 'received' ? 'reviewer' : 'reviewed'].photo}
                    username={review[currentReviewTab === 'received' ? 'reviewer' : 'reviewed'].username}
                    text={review.review}
                    rate={review.rate}
                    date={review.created_at * 1000}
                    itemPhoto={review.item.cover_image || USER_COVER_PLACEHOLDER_LINK}
                    itemSlug={review.item.slug}
                  />
                ))}
                {((currentReviewTab === 'given' && givenPage < givenLastPage) ||
                  (currentReviewTab === 'received' && receivedPage < receivedLastPage)) && (
                  <LazyLoader isActive={!isLoading} callback={loadMore} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
